'use client';

import { FC, useState, useRef } from 'react'
import { Button, Dialog, DialogContent, DialogTitle } from '@/components/ui'
import { Maximize2, Upload } from 'lucide-react'
import { DataTableInput } from '../data-table-input/data-table-input'
import { TableDataChangeProps, TableDataJSON } from '../../../src/types/table-json'
import React from 'react'
import { createDataTableStore } from '../data-table-input/data-table-input.store'
import { useWindowSize } from 'usehooks-ts'
import { handleCSVFileSelect } from '../../../lib/csv/csv-parser'

interface DataPanelProps extends TableDataChangeProps {
  initialColumns?: { id: string; name: string; width?: number }[]
  initialData?: TableDataJSON
  maxWidth?: number
  maxHeight?: number
  resetControlValues?: () => void
}

export const DataPanel: FC<DataPanelProps> = ({ 
  onDataChange,
  initialColumns,
  initialData,
  maxWidth,
  maxHeight,
  resetControlValues
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { height: windowHeight, width: windowWidth } = useWindowSize()
  const dataTableRef = React.useRef<{ clearData: () => void; importData: (data: TableDataJSON) => void }>(null)
  const storeRef = React.useRef<ReturnType<typeof createDataTableStore>>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleCSVClick = () => {
    // Trigger the hidden file input
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      // Parse the CSV file
      const tableData = await handleCSVFileSelect(event);
      
      if (tableData) {
        // First, call onDataChange to update the calculator's data
        if (onDataChange) {
          onDataChange(tableData);
        }
        
        // Then reset control values to match the new data structure
        if (resetControlValues) {
          // Use setTimeout to ensure this runs after the data update has been processed
          setTimeout(() => {
            resetControlValues();
          }, 0);
        }
        
        // Finally, import the data into the table
        if (dataTableRef.current) {
          dataTableRef.current.importData(tableData);
        }
      }
      
      // Reset the file input so the same file can be selected again
      event.target.value = '';
    } catch (error) {
      console.error('Error handling CSV file:', error);
      // You could add error handling UI here
    }
  };


  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      <div className="flex items-center justify-between px-2 pb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium text-foreground">Input data</h2>
        </div>
        <div className="flex gap-1">
          {/* Hidden file input for CSV upload */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".csv"
            className="hidden"
          />
          <Button
            variant="ghost"
            onClick={handleCSVClick}
            aria-label="Upload CSV"
          >
            <Upload className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <Button
            variant="ghost"
            onClick={() => dataTableRef.current?.clearData()}
          >
            Clear
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            onClick={() => setIsModalOpen(true)}
            aria-label="Maximize data panel"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Regular view */}
      <div className="flex-1 min-h-0">
        <DataTableInput
          ref={dataTableRef}
          storeRef={storeRef}
          onChange={onDataChange}
          initialData={initialData || TableDataJSON.from({
            columns: initialColumns?.map(col => ({
              id: col.id,
              name: col.name,
              values: []
            })) || []
          })}
          maxWidth={maxWidth ? maxWidth - 16 : undefined}
          maxHeight={maxHeight}
        />
      </div>

      {/* Modal view */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="p-0 w-full max-w-screen-2xl h-[80vh] flex flex-col">
          <DialogTitle className="px-6 pt-6 flex-none font-medium tracking-normal">Input Data</DialogTitle>
          <div className="px-6 pb-6 flex-1 min-h-0">
            <DataTableInput
              storeRef={storeRef}
              onChange={onDataChange}
              initialData={initialData || TableDataJSON.from({
                columns: initialColumns?.map(col => ({
                  id: col.id,
                  name: col.name,
                  values: []
                })) || []
              })}
              maxWidth={windowWidth ? Math.min(windowWidth * 0.95 - 48, 1488) : 1488}
              maxHeight={windowHeight ? Math.floor(windowHeight * 0.8) - 120 : 600}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
} 